import { createElement } from 'react';
import type { ThemeOptions } from '@mui/material/styles';
import type { MultiSectionDigitalClockProps } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import type { DateTime } from 'luxon';
// Path imports, not the `@mui/icons-material` barrel: the barrel re-exports
// ~10,600 modules, and this file is reachable from the theme, which every test
// loads via setupTests. Importing the barrel here cost ~40s of module loading
// in *every* test file.
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { paletteTheme as baseTheme } from './palette';
import { paletteTheme as mvpBaseTheme } from './palette.mvp';
import { paletteTheme as dentaquestBaseTheme } from './palette.dentaquest';
import { paletteTheme as ahcBaseTheme } from './palette.ahc';
import { paletteTheme as ahcadminBaseTheme } from './palette.ahcadmin';
import { paletteTheme as transportBaseTheme } from './palette.transport';
import { usePortalName } from '../../hooks/usePortalName';

const portalName = usePortalName();
let theme = baseTheme;
switch (portalName) {
  case 'mvp':
    theme = mvpBaseTheme;
    break;
  case 'dentaquest':
    theme = dentaquestBaseTheme;
    break;
  case 'ahc':
    theme = ahcBaseTheme;
    break;
  case 'ahcadmin':
    theme = ahcadminBaseTheme;
    break;
  case 'transport':
    theme = transportBaseTheme;
    break;
}
const components: ThemeOptions['components'] = {
  MuiPaper: {
    styleOverrides: {
      root: {
        color: theme.palette.lightTextPrimary.main,
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 400,
        fontSize: 14,
        color: theme.palette.buttonColor.main,
      },
    },
  },
  MuiMultiSectionDigitalClock: {
    styleOverrides: {
      root: {
        '&&': {
          '.MuiList-root': {
            width: '65px',
          },
        },
      },
    },
  } as MultiSectionDigitalClockProps<DateTime>,
  MuiButton: {
    defaultProps: {
      focusRipple: false,
    },
    styleOverrides: {
      root: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        fontSize: 14,
        fontStyle: 'normal',
        lineHeight: '24px',
        letterSpacing: '0.46px',
        textTransform: 'unset',
        border: `1px solid transparent`,
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
        whiteSpace: 'nowrap',
        backgroundColor: theme.palette.buttonColor.main,
        '&:hover': {
          backgroundColor: theme.palette.buttonHoverColor.main,
        },
        '&.Mui-disabled': {
          background: theme.palette.lightActionDisabled12p.main,
          border: `1px solid transparent`,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '3px',
          borderRadius: '2px',
          boxShadow: 'none',
        },
      },
      containedSecondary: {
        boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        fontSize: 14,
        fontStyle: 'normal',
        lineHeight: '24px',
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.buttonColor.main}`,
        color: theme.palette.buttonColor.main,
        '&:hover': {
          boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled54p.main}`,
          backgroundColor: theme.palette.secondary.main,
        },
        [theme.breakpoints.down('md')]: {
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          paddingLeft: theme.spacing(1.75),
          paddingRight: theme.spacing(1.75),
        },
        '&.min-width-home-recent': {
          minWidth: theme.spacing(16.97875), // '135.83px',
          [theme.breakpoints.down('md')]: {
            minWidth: theme.spacing(9),
          },
          '&:hover': {
            backgroundColor: '#F5F5F5',
          },
        },
      },
      outlined: {
        boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
        borderRadius: '3px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.buttonColor.main,
        color: theme.palette.buttonColor.main,
        backgroundColor: 'white',
        '&:hover': {
          borderColor: theme.palette.buttonHoverColor.main,
          background: theme.palette.lightPrimaryShades04p.main,
        },
        '&.Mui-disabled': {
          background: 'white',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: theme.palette.lightActionDisabled12p.main,
          boxShadow: 'none',
          color: theme.palette.lightActionDisabled26p.main,
        },
      },
      text: {
        color: theme.palette.buttonColor.main,
        backgroundColor: 'white',
        '&:hover': {
          background: 'white',
        },
      },
      sizeSmall: {
        fontSize: 13,
        height: '32px',
        width: 'auto',
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        boxShadow: 'none',
      },
      sizeLarge: {
        fontSize: 15,
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        color: theme.palette.lightTextPrimary.main,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '&.rounded-more': {
          backgroundColor: theme.palette.lightActionDisabled04p.main,
          width: theme.spacing(6),
          height: theme.spacing(6),
        },
        '&.rounded-more:hover': {
          backgroundColor: theme.palette.lightActionDisabled12p.main,
        },
      },
      colorPrimary: {
        color: theme.palette.buttonColor.main,
      },
    },
  },
  MuiTabs: {
    variants: [
      {
        props: { variant: 'standard' },
        style: {
          '& .MuiTab-root': {
            minWidth: 264,
          },
        },
      },
    ],
    styleOverrides: {
      flexContainer: {
        borderBottom: `1px solid ${theme.palette.lightActionDisabled12p.main}`,
      },
      indicator: {
        backgroundColor: theme.palette.buttonColor.main,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'unset',
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        letterSpacing: '0.4px',
        lineHeight: '24px',
        '&.Mui-selected': {
          color: theme.palette.buttonColor.main,
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        color: theme.palette.lightTextPrimary.main,
        fontFamily: 'Source Sans Pro',
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none',
        },
      },
    },
  },
  MuiStepper: {
    styleOverrides: {
      root: {
        backgroundColor: '#fff',
        borderRadius: 100,
        padding: 32,
        ['&.no-padding']: {
          padding: 0,
        },
        ['&.auto-connector-width .MuiStepConnector-root']: {
          width: 'auto',
        },
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      iconContainer: {
        fill: 'rgba(0, 0, 0, 0.38)',
        color: 'rgba(0, 0, 0, 0.38)',
        '&.Mui-active': {
          fill: '#000',
          color: '#000',
        },
        '&.Mui-completed': {
          fill: '#2E7D32',
          color: '#2E7D32',
        },
        svg: {
          text: {
            fontSize: 12,
          },
        },
      },
      label: {
        fontSize: '16px',
        ['&.MuiStepLabel-alternativeLabel']: {
          marginTop: theme.spacing(1),
        },
        ['&.Mui-completed']: {
          fontWeight: 600,
          lineHeight: '175%',
          letterSpacing: '0.15px',
        },
        ['&.Mui-active']: {
          fontWeight: 600,
          lineHeight: '175%',
          letterSpacing: '0.15px',
          color: theme.palette.lightTextPrimary.main,
        },
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      root: {
        width: theme.spacing(7.18),
      },
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        '&.MuiStepIcon-root': {
          fill: 'unset',
        },
      },
      colorAction: {
        color: theme.palette.lightActionDisabled54p.main,
      },
    },
  },
  MuiToggleButton: {
    defaultProps: {
      focusRipple: false,
    },
    styleOverrides: {
      root: {
        textTransform: 'capitalize',
        flex: 1,
        padding: '8px',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '150%',
        letterSpacing: '0.15px',
        '&.white-background': {
          backgroundColor: theme.palette.secondary.main,
          border: '1px solid rgba(0, 0, 0, 0.12)',
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.buttonColor.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.buttonHoverColor.main,
          },
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '3px',
          borderRadius: '2px',
          boxShadow: 'none',
        },
      },
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        width: '100%',
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: {
        color: theme.palette.lightTextPrimary.main,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '3px',
        boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
        padding: theme.spacing(2),
        ['&.bg-primary-shade']: {
          backgroundColor: theme.palette.primaryCardBackground.main,
        },
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        paddingTop: theme.spacing(1),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        ':last-child': {
          paddingBottom: theme.spacing(1),
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        paddingLeft: theme.spacing(4),
        paddingTop: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        fontFamily: 'Bitter',
        fontSize: '30px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '40px',
        letterSpacing: '0.25px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        border: 'none',
        padding: 0,
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(2),
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        paddingTop: theme.spacing(2),
        justifyContent: 'flex-start',
        gap: theme.spacing(2),
        '& button': {
          flex: '1',
        },
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        height: 8,
        borderRadius: 4,
        '&.MuiLinearProgress-colorPrimary': {
          backgroundColor: theme.palette.grey[200],
          ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
          }),
        },
        '& .MuiLinearProgress-bar': {
          borderRadius: 5,
          backgroundColor: theme.palette.primary.main,
          ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.primary.main,
          }),
        },
      },
    },
  },
  MuiFormGroup: {
    styleOverrides: {
      root: {
        '&.boolean-radio-group': {
          '& .radio-item:has(.radio-helper-text)': {
            paddingBottom: theme.spacing(2),
          },
          '& .MuiTypography-body1': {
            fontSize: '16px',
            lineHeight: '150%',
            letterSpacing: '0.15px',
            paddingBottom: 0,
          },
          '& .radio-helper-text': {
            display: 'block',
            paddingLeft: theme.spacing(4),
            marginTop: theme.spacing(-1),
            fontSize: '14px',
            lineHeight: '143%',
            letterSpacing: '0.17px',
            color: theme.palette.lightActionDisabled60p.main,
          },
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        transform: 'translate(14px, 11px) scale(1)',
        color: theme.palette.lightTextSecondary.main,
      },
      asterisk: {
        paddingLeft: '1px',
        color: theme.palette.error.main,
      },
      filled: {
        color: theme.palette.lightTextSecondary.main,
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        width: ['-moz-available', '-webkit-fill-available'],
        '&:has(label.MuiFormLabel-filled) input': {
          paddingBottom: theme.spacing(0.5),
          paddingTop: theme.spacing(2.5),
        },
        '&:has(label.Mui-focused) input': {
          paddingBottom: theme.spacing(0.5),
          paddingTop: theme.spacing(2.5),
        },
        '&:has(label.MuiFormLabel-filled) textarea': {
          paddingBottom: theme.spacing(0.5),
          paddingTop: theme.spacing(2.5),
        },
        '&:has(label.Mui-focused) textarea': {
          paddingBottom: theme.spacing(0.5),
          paddingTop: theme.spacing(2.5),
        },
        '& .MuiTelInput--disableDropdown .MuiInputAdornment-root': {
          display: 'none',
        },
        '& input::-ms-reveal, & input::-ms-clear': {
          display: 'none',
        },
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        '&:has(.multi-line)': {
          alignItems: 'flex-start',
        },

        '&:has(.multi-line) .MuiCheckbox-root': {
          marginTop: '2px',
        },

        '&:has(.multi-line) .MuiTypography-root': {
          paddingTop: theme.spacing(0.5),
        },

        '&:has(.MuiCheckbox-root) .MuiTypography-body1': {
          paddingBottom: 0,
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        textAlign: 'left',
        margin: 0,
        '&.Mui-error': {
          paddingLeft: theme.spacing(1.5),
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        border: 'none',
        minHeight: '48px',
        padding: 0,
        '.MuiInputAdornment-positionEnd': {
          paddingRight: theme.spacing(2),
        },
        '.MuiSelect-outlined': {
          paddingTop: theme.spacing(2.5),
          paddingBottom: theme.spacing(0.5),
        },
        '&.Mui-readOnly .MuiOutlinedInput-notchedOutline': {
          boxShadow: 'none',
        },
        '&.Mui-readOnly input, &.MuiInputBase-readOnly': {
          background: theme.palette.grey[100],
        },
        '&.Mui-readOnly .MuiButtonBase-root.Mui-disabled': {
          color: 'unset',
        },
      },
      notchedOutline: {
        border: 'none',
        borderRadius: '3px',
        boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled15p.main}`,
      },
      input: {
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        color: theme.palette.lightTextPrimary.main,
        padding: theme.spacing(1.5),
        '&.Mui-focused': {
          paddingTop: theme.spacing(0.75),
          paddingRight: theme.spacing(1.5),
          paddingBottom: theme.spacing(0.5),
          paddingLeft: theme.spacing(1.4),
        },
        '&.MuiFormLabel-filled': {
          paddingTop: theme.spacing(0.75),
          paddingRight: theme.spacing(1.5),
          paddingBottom: theme.spacing(0.5),
          paddingLeft: theme.spacing(1.4),
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        border: 'none',
        padding: 0,
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        transform: 'translate(14px, 11px) scale(1)',
        '&.Mui-focused': {
          transform: 'translate(12px, 8px) scale(1)',
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '12px',
        },
        '&.MuiFormLabel-filled': {
          transform: 'translate(12px, 8px) scale(1)',
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '12px',
        },
        '&.MuiInputLabel-shrink': {
          lineHeight: '12px',
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      // Dont set overflow to hidden if multiline (textarea)
      input: ({ ownerState }) => ({
        ...(ownerState.multiline !== true && {
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }),
      }),
    },
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: ExpandMoreRounded,
    },
    styleOverrides: {
      select: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '.MuiOutlinedInput-root': {
          padding: 0,
          '.MuiAutocomplete-input': {
            paddingLeft: theme.spacing(1.5),
            paddingBottom: theme.spacing(0.5),
            paddingTop: theme.spacing(2.5),
          },
        },
      },
      popupIndicator: {
        '&:has(.transform-none)': {
          transform: 'none',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        '& .MuiChip-label.MuiChip-labelMedium': {
          color: theme.palette.secondary.main,
          fontFamily: 'Source Sans Pro',
          fontSize: '13px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '18px',
          letterSpacing: '0.16px',
        },
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        color: theme.palette.lightTextPrimary.main,
        '&.transform-capitalize': {
          textTransform: 'capitalize',
        },
        '&.light-gray': {
          color: theme.palette.lightActionDisabled60p.main,
        },
        '&.light-gray-e5': {
          color: theme.palette.lightActionDisabled90p.main,
        },
        '&.bold, & .bold': {
          fontWeight: 600,
        },
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
        ['&:first-of-type ']: {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        },
        ['&:last-of-type ']: {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
        a: {
          color: theme.palette.buttonColor.main,
          textDecoration: 'none',
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        '&.Mui-expanded': {
          margin: 0,
          borderTop: `1px solid ${theme.palette.lightActionDisabled12p.main}`,
          '.MuiCollapse-root': {
            marginBottom: theme.spacing(1.5),
          },
          '&.Mui-expanded:first-of-type': {
            borderTop: 'none',
          },
        },
      },
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        padding: 0,
      },
      content: {
        ['&.Mui-expanded']: {
          margin: theme.spacing(1.5, 0),
        },
      },
      expandIconWrapper: {
        marginRight: theme.spacing(2),
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiAlert: {
    defaultProps: {
      iconMapping: {
        success: createElement(CheckCircleOutline),
        info: createElement(InfoOutlined),
        warning: createElement(WarningAmberRounded),
        error: createElement(InfoOutlined),
      },
    },
    styleOverrides: {
      root: {
        boxShadow: `0px 2px 7px 0px ${theme.palette.lightActionDisabled26p.main}`,
        paddingInline: theme.spacing(6),
        '& .MuiAlert-icon': {
          color: theme.palette.success.main,
        },
        '&.site-banner': {
          paddingInline: theme.spacing(2),

          '&.MuiAlert-outlinedError': {
            '& .MuiAlert-icon': {
              color: theme.palette.error.main,
            },
          },

          '& .MuiAlert-message': {
            fontFamily: 'Source Sans Pro',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0.15px',
            color: theme.palette.lightTextPrimary.main,
          },
          [theme.breakpoints.down('sm')]: {
            '& .MuiTypography-root:first-of-type': {
              paddingBottom: theme.spacing(0.5),
            },
          },
        },
      },
      filledSuccess: {
        backgroundColor: theme.palette.success.main,
        backgroundImage: `linear-gradient(0deg, ${theme.palette.whiteOverlay90p.main}, ${theme.palette.whiteOverlay90p.main})`,
        color: theme.palette.common.black,
        '& .MuiAlert-icon': {
          color: theme.palette.success.main,
        },
      },
      filledInfo: {
        backgroundColor: theme.palette.info.main,
        backgroundImage: `linear-gradient(0deg, ${theme.palette.whiteOverlay90p.main}, ${theme.palette.whiteOverlay90p.main})`,
        color: theme.palette.common.black,
        '& .MuiAlert-icon': {
          color: theme.palette.info.main,
        },
      },
      filledWarning: {
        backgroundColor: theme.palette.warning.main,
        backgroundImage: `linear-gradient(0deg, ${theme.palette.whiteOverlay90p.main}, ${theme.palette.whiteOverlay90p.main})`,
        color: theme.palette.common.black,
        '& .MuiAlert-icon': {
          color: theme.palette.warning.main,
        },
      },
      filledError: {
        backgroundColor: theme.palette.error.main,
        backgroundImage: `linear-gradient(0deg, ${theme.palette.whiteOverlay90p.main}, ${theme.palette.whiteOverlay90p.main})`,
        color: theme.palette.common.black,
        '& .MuiAlert-icon': {
          color: theme.palette.grey['600'],
        },
      },
    },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: {
        '& .MuiSnackbarContent-root': {
          '&.MuiSnackbarContent-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            padding: 0,
          },
        },
        '& .MuiAlert-icon': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          marginRight: theme.spacing(2.5),
        },
        '& .MuiAlert-message': {
          fontFamily: 'Source Sans Pro',
          fontStyle: 'normal',
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: 600,
          letterSpacing: '0.15px',
        },
      },
    },
  },

  MuiList: {
    styleOverrides: {
      root: {
        '&.MuiMenu-list': {
          paddingTop: '0px !important',
          paddingBottom: '0px !important',
          '& li': {
            paddingTop: theme.spacing(1.5),
            paddingBottom: theme.spacing(1.5),
          },
          '& .MuiDivider-root': {
            margin: 0,
          },
          '& .Mui-focusVisible': {
            backgroundColor: 'none',
          },
        },
      },
    },
  },
} as ThemeOptions['components'];

export { components };
